import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { trpc } from "@/lib/trpc";
import { useCompany } from "@/contexts/CompanyContext";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  TrendingUp,
  CheckCircle2,
  XCircle,
  Clock,
  Sparkles,
  FileText,
  AlertCircle,
  ChevronRight,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

export default function TaxOptimization() {
  return (
    <DashboardLayout>
      <TaxOptimizationContent />
    </DashboardLayout>
  );
}

function TaxOptimizationContent() {
  const { selectedCompanyId } = useCompany();
  const [taxYear] = useState(new Date().getFullYear());

  // Fetch companies (for empty state check)
  const { data: companies } = trpc.company.list.useQuery();

  // Fetch optimization summary
  const { data: summary, refetch: refetchSummary } =
    trpc.deductionOptimizer.getOptimizationSummary.useQuery(
      { companyId: selectedCompanyId!, taxYear },
      { enabled: !!selectedCompanyId }
    );

  // Fetch pending suggestions
  const { data: pendingSuggestions, refetch: refetchPending } =
    trpc.deductionOptimizer.getPendingSuggestions.useQuery(
      { companyId: selectedCompanyId!, taxYear },
      { enabled: !!selectedCompanyId }
    );

  // Fetch all suggestions
  const { data: allSuggestions, refetch: refetchAll } =
    trpc.deductionOptimizer.getAllSuggestions.useQuery(
      { companyId: selectedCompanyId!, taxYear },
      { enabled: !!selectedCompanyId }
    );

  // Generate suggestions mutation
  const generateMutation =
    trpc.deductionOptimizer.generateSuggestions.useMutation({
      onSuccess: data => {
        toast.success("Suggestions Generated", {
          description: `Found ${data.count} optimization opportunities`,
        });
        refetchSummary();
        refetchPending();
        refetchAll();
      },
      onError: error => {
        toast.error("Generation Failed", {
          description: error.message,
        });
      },
    });

  // Apply suggestion mutation
  const applyMutation = trpc.deductionOptimizer.applySuggestion.useMutation({
    onSuccess: () => {
      toast.success("Suggestion Applied", {
        description: "Deduction has been marked as applied",
      });
      refetchSummary();
      refetchPending();
      refetchAll();
    },
  });

  // Reject suggestion mutation
  const rejectMutation = trpc.deductionOptimizer.rejectSuggestion.useMutation({
    onSuccess: () => {
      toast.success("Suggestion Rejected", {
        description: "Suggestion has been marked as rejected",
      });
      refetchSummary();
      refetchPending();
      refetchAll();
    },
  });

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat("de-CH", {
      style: "currency",
      currency: "CHF",
    }).format(cents / 100);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "critical":
        return "destructive";
      case "high":
        return "default";
      case "medium":
        return "secondary";
      case "low":
        return "outline";
      default:
        return "secondary";
    }
  };

  const getConfidenceBadge = (score: number) => {
    if (score >= 90)
      return (
        <Badge variant="default" className="bg-success">
          High Confidence
        </Badge>
      );
    if (score >= 70)
      return <Badge variant="secondary">Medium Confidence</Badge>;
    return <Badge variant="outline">Low Confidence</Badge>;
  };

  if (!companies || companies.length === 0) {
    return (
      <div className="container py-8">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Please create a company first to access tax optimization features.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Tax Optimization</h1>
          <p className="text-muted-foreground mt-1">
            Smart suggestions to minimize your Swiss corporate tax burden
          </p>
        </div>
        <Button
          onClick={() =>
            generateMutation.mutate({ companyId: selectedCompanyId!, taxYear })
          }
          disabled={generateMutation.isPending}
        >
          {generateMutation.isPending ? (
            <>
              <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              Analyzing...
            </>
          ) : (
            <>
              <Sparkles className="mr-2 h-4 w-4" />
              Generate Suggestions
            </>
          )}
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Potential Savings
              </CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(summary.totalPotentialSavings)}
              </div>
              <p className="text-xs text-muted-foreground">
                From {summary.pendingCount} pending suggestions
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Applied Savings
              </CardTitle>
              <CheckCircle2 className="h-4 w-4 text-success" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(summary.appliedSavings)}
              </div>
              <p className="text-xs text-muted-foreground">
                From {summary.appliedCount} applied deductions
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Pending Review
              </CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary.pendingCount}</div>
              <p className="text-xs text-muted-foreground">
                Suggestions awaiting action
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Rejected</CardTitle>
              <XCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary.rejectedCount}</div>
              <p className="text-xs text-muted-foreground">Not applicable</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Suggestions Tabs */}
      <Tabs defaultValue="pending" className="space-y-4">
        <TabsList>
          <TabsTrigger value="pending">
            Pending ({pendingSuggestions?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="all">All Suggestions</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="space-y-4">
          {!pendingSuggestions || pendingSuggestions.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <Sparkles className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  No pending suggestions. Click "Generate Suggestions" to
                  analyze your transactions.
                </p>
              </CardContent>
            </Card>
          ) : (
            pendingSuggestions.map(suggestion => (
              <Card key={suggestion.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <CardTitle>{suggestion.title}</CardTitle>
                        <Badge
                          variant={getPriorityColor(
                            suggestion.priority || "medium"
                          )}
                        >
                          {suggestion.priority || "medium"}
                        </Badge>
                        {getConfidenceBadge(suggestion.confidenceScore)}
                      </div>
                      <CardDescription>
                        {suggestion.description}
                      </CardDescription>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-success">
                        {formatCurrency(suggestion.estimatedSavings || 0)}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Estimated Savings
                      </p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Calculation Details */}
                  {suggestion.calculationDetails && (
                    <div className="bg-muted p-4 rounded-lg">
                      <h4 className="font-semibold mb-2">Calculation</h4>
                      <p className="text-sm text-muted-foreground mb-2">
                        {(suggestion.calculationDetails as any).formula}
                      </p>
                      {(suggestion.calculationDetails as any).breakdown && (
                        <div className="space-y-1">
                          {(
                            (suggestion.calculationDetails as any)
                              .breakdown as Array<{
                              label: string;
                              value: number;
                            }>
                          ).map((item, i) => (
                            <div
                              key={i}
                              className="flex justify-between text-sm"
                            >
                              <span>{item.label}:</span>
                              <span className="font-medium">
                                {typeof item.value === "number" &&
                                item.value > 1000
                                  ? formatCurrency(item.value)
                                  : item.value}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Required Documents */}
                  {suggestion.requiredDocuments &&
                    (suggestion.requiredDocuments as any[]).length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-2 flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          Required Documents
                        </h4>
                        <ul className="space-y-1">
                          {(
                            suggestion.requiredDocuments as Array<{
                              documentType: string;
                              description: string;
                              isMandatory: boolean;
                            }>
                          ).map((doc, i) => (
                            <li
                              key={i}
                              className="text-sm flex items-start gap-2"
                            >
                              <ChevronRight className="h-4 w-4 mt-0.5 flex-shrink-0" />
                              <span>
                                {doc.description}
                                {doc.isMandatory && (
                                  <Badge variant="outline" className="ml-2">
                                    Required
                                  </Badge>
                                )}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                  {/* Action Steps */}
                  {suggestion.actionSteps &&
                    (suggestion.actionSteps as any[]).length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-2">Action Steps</h4>
                        <ol className="space-y-1 list-decimal list-inside">
                          {(suggestion.actionSteps as string[]).map(
                            (step, i) => (
                              <li
                                key={i}
                                className="text-sm text-muted-foreground"
                              >
                                {step}
                              </li>
                            )
                          )}
                        </ol>
                      </div>
                    )}

                  {/* Actions */}
                  <div className="flex gap-2 pt-4 border-t">
                    <Button
                      onClick={() =>
                        applyMutation.mutate({ suggestionId: suggestion.id })
                      }
                      disabled={applyMutation.isPending}
                    >
                      <CheckCircle2 className="mr-2 h-4 w-4" />
                      Apply Deduction
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() =>
                        rejectMutation.mutate({ suggestionId: suggestion.id })
                      }
                      disabled={rejectMutation.isPending}
                    >
                      <XCircle className="mr-2 h-4 w-4" />
                      Not Applicable
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        <TabsContent value="all" className="space-y-4">
          {!allSuggestions || allSuggestions.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <p className="text-muted-foreground">No suggestions yet.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {allSuggestions.map(suggestion => (
                <Card key={suggestion.id}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-base">
                          {suggestion.title}
                        </CardTitle>
                        <Badge
                          variant={
                            suggestion.status === "applied"
                              ? "default"
                              : suggestion.status === "rejected"
                                ? "destructive"
                                : "secondary"
                          }
                        >
                          {suggestion.status}
                        </Badge>
                      </div>
                      <div className="text-right">
                        <div className="font-bold">
                          {formatCurrency(suggestion.estimatedSavings || 0)}
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
